import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import ToolCard from '../components/ToolCard';
import { fetchCategories, fetchTools } from '../api/client';

// Fast tag filters shown as chips at the top of a category page.
// Each chip maps to one or more query params that the API understands.
const TAG_FILTERS = [
  { key: 'free',       label: 'Free',        params: { pricing: 'free' } },
  { key: 'freemium',   label: 'Freemium',    params: { pricing: 'freemium' } },
  { key: 'paid',       label: 'Paid',        params: { pricing: 'paid' } },
  { key: 'open-source',label: 'Open source', params: { pricing: 'open-source' } },
  { key: 'api',        label: 'API available', params: { tag: 'api' } },
  { key: 'featured',   label: 'Featured',    params: { featured: 'true' } },
  { key: 'verified',   label: 'Verified',    params: { tag: 'verified' } },
];

export default function CategoryPage() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const sub = searchParams.get('sub') || '';
  const pricing = searchParams.get('pricing') || '';
  const tagParam = searchParams.get('tag') || '';
  const featuredParam = searchParams.get('featured') || '';
  const sort = searchParams.get('sort') || 'popular';

  const [categories, setCategories] = useState([]);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState(q);

  const cat = useMemo(() => categories.find((c) => c.slug === slug), [categories, slug]);
  const isAll = slug === 'all';

  useEffect(() => {
    fetchCategories().then(setCategories);
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 120, sort };
    if (!isAll && cat) params.category = cat.name;
    if (sub) params.subcategory = sub;
    if (q) params.q = q;
    if (pricing) params.pricing = pricing;
    if (tagParam) params.tag = tagParam;
    if (featuredParam === 'true') params.featured = true;
    fetchTools(params).then((data) => {
      setTools(data);
      setLoading(false);
    });
  }, [isAll, cat, sub, q, pricing, tagParam, featuredParam, sort]);

  const updateParam = (k, v) => {
    const p = new URLSearchParams(searchParams);
    if (v) p.set(k, v);
    else p.delete(k);
    setSearchParams(p);
  };

  const isFilterActive = (f) =>
    Object.entries(f.params).every(([k, v]) => (searchParams.get(k) || '') === v);

  const toggleFilter = (f) => {
    const p = new URLSearchParams(searchParams);
    if (isFilterActive(f)) {
      Object.keys(f.params).forEach((k) => p.delete(k));
    } else {
      // Clear other filters of the same param family to avoid impossible combos
      Object.keys(f.params).forEach((k) => p.delete(k));
      Object.entries(f.params).forEach(([k, v]) => p.set(k, v));
    }
    setSearchParams(p);
  };

  const clearAll = () => {
    const p = new URLSearchParams();
    if (sort && sort !== 'popular') p.set('sort', sort);
    setSearchParams(p);
    setQuery('');
  };

  const anyFilterActive = q || pricing || tagParam || featuredParam || sub;

  const submitSearch = (e) => {
    e.preventDefault();
    updateParam('q', query.trim());
  };

  const heading = isAll ? 'All AI Tools' : cat?.name || 'Category';

  return (
    <div className="page-container">
      <div className="crumbs">
        <Link to="/">Home</Link>
        <span>/</span>
        <Link to="/browse">Browse</Link>
        <span>/</span>
        <span>{heading}</span>
      </div>
      <h1 className="page-title">{heading}</h1>

      <form className="cat-search" onSubmit={submitSearch} data-testid="cat-search">
        <Search size={14} className="cat-search-icon" />
        <input
          type="text"
          placeholder={`Search ${isAll ? 'all tools' : heading.toLowerCase()}…`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="cat-search-input"
        />
        {query && (
          <button type="button" className="cat-search-clear" onClick={() => { setQuery(''); updateParam('q', ''); }} aria-label="Clear">
            <X size={12} />
          </button>
        )}
        <button type="submit" className="pill-btn primary">Search</button>
      </form>

      <div className="cat-filter-chips" data-testid="cat-filter-chips">
        {TAG_FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`cat-filter-chip ${isFilterActive(f) ? 'active' : ''}`}
            onClick={() => toggleFilter(f)}
            data-testid={`cat-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
        {anyFilterActive && (
          <button type="button" className="cat-filter-clear" onClick={clearAll} data-testid="cat-clear">
            Clear all
          </button>
        )}
      </div>

      <div className="filter-row">
        {cat?.subcategories?.length > 0 && (
          <select value={sub} onChange={(e) => updateParam('sub', e.target.value)}>
            <option value="">All subcategories</option>
            {cat.subcategories.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        )}
        <select value={sort} onChange={(e) => updateParam('sort', e.target.value)}>
          <option value="popular">Most popular</option>
          <option value="recent">Recently added</option>
          <option value="name">Alphabetical</option>
          <option value="featured">Featured first</option>
        </select>
        <span className="result-count">
          {loading ? 'Loading…' : `${tools.length} tool${tools.length === 1 ? '' : 's'}`}
        </span>
      </div>

      {isAll && (
        <div className="category-chips">
          <Link to="/category/all" className={`chip ${isAll ? 'active' : ''}`}>All</Link>
          {categories.map((c) => (
            <Link key={c.slug} to={`/category/${c.slug}`} className="chip">
              {c.name} <span className="chip-count">{c.count}</span>
            </Link>
          ))}
        </div>
      )}

      <div className="tool-grid">
        {tools.map((t) => (
          <ToolCard key={t.id} tool={t} />
        ))}
      </div>
      {!loading && tools.length === 0 && (
        <div className="empty-state">No tools match your filters.</div>
      )}
    </div>
  );
}
